'use client'

import { useMemo, useRef, useState } from 'react'

import {
  Box,
  Button,
  Card,
  Field,
  Group,
  HStack,
  IconButton,
  Select,
  Spacer,
  Stack,
  Tabs,
  Text,
  createListCollection,
} from '@chakra-ui/react'
import { createId } from '@paralleldrive/cuid2'
import { useQuery, useZero } from '@rocicorp/zero/react'
import { Switch, toast } from '@saas-ui/react'
import { useHotkeysShortcut } from '@saas-ui/use-hotkeys'
import { useNavigate } from '@tanstack/react-router'

import { ArrowUpIcon } from '#components/icons/arrow-up-icon.tsx'
import { BoltIcon } from '#components/icons/bolt-icon.tsx'
import { BugIcon } from '#components/icons/bug-icon.tsx'
import { CodeCircleIcon } from '#components/icons/code-circle-icon.tsx'
import { Infinity2Icon } from '#components/icons/infinity-2-icon.tsx'
import { PuzzleIcon } from '#components/icons/puzzle-icon.tsx'
import { BranchSelect } from '#features/common/components/branch-select'
import { ModelSelect } from '#features/common/components/model-select'
import {
  type BranchChoice,
  type ComposerMode,
  defaultBranchSelection,
  isStartDisabled,
  resolveBaseBranchById,
  resolveBlueprintId,
  resolveDefaultRepoSelection,
  resolveInheritedDefaultModelSelection,
  resolveSelectedBranch,
  resolveStepConfigOverrides,
  resolveSubmittedTaskModel,
  shouldShowAutoAcceptPlan,
  uniqueBlueprintsByName,
} from '#features/common/components/prompt-composer-state.ts'
import {
  PromptEditor,
  type PromptEditorRef,
} from '#features/common/components/prompt-editor.tsx'
import { buildRepoSelection } from '#features/common/components/repo-selection.ts'
import { RepositorySelect } from '#features/common/components/repository-select'
import { useCurrentUser } from '#features/common/hooks/use-current-user'
import { useCurrentWorkspace } from '#features/common/hooks/use-current-workspace.ts'
import { useGitHubRepositories } from '#features/common/hooks/use-github-repositories.ts'
import { useLastSelectedModel } from '#lib/last-selected-model.ts'
import { useLastSelectedRepository } from '#lib/last-selected-repository.ts'
import { api } from '#lib/trpc/react'
import { awaitServerMutation } from '#zero/await-server-mutation.ts'
import { AUTO_BLUEPRINT_ID, mutators } from '#zero/mutators.ts'
import { queries } from '#zero/queries.ts'

const blueprintTypeIcons: Record<string, typeof BugIcon> = {
  bug_fix: BugIcon,
  feature: PuzzleIcon,
  quick_change: BoltIcon,
}

/**
 * The static "Auto" picker entry. It is not a real catalog blueprint — its
 * sentinel id flows through `startAdHoc` unchanged and is resolved to a concrete
 * blueprint server-side. Kept in the `Select` collection (so `ValueText` renders
 * "Auto" and the option is keyboard-navigable) but deliberately excluded from
 * `composerBlueprints`, so `selectedBlueprint` stays `undefined` for Auto.
 */
const autoBlueprintItem = { id: AUTO_BLUEPRINT_ID, name: 'Auto' }

export interface PromptComposerProps {
  /**
   * Called once a session/task has been created. The create dialog uses this to
   * close itself; the home screen has nothing to clean up.
   *
   * For sessions this runs immediately before navigating to the new session.
   * For tasks it runs after the success toast — and only navigates when
   * `navigateToTask` is true.
   */
  onCreated?: () => void
  /**
   * Own the global "focus prompt" hotkey. Only one mounted composer may do so —
   * two handlers would fight over which editor receives focus — so the home
   * screen opts in and the dialog (which already autofocuses) does not.
   */
  focusHotkey?: boolean
  /**
   * The tab the composer starts on; the user can still switch afterwards.
   *
   * The home screen and the sidebar's create button start on Task. The sessions
   * list page opens this same composer on Session, so its "New session" button
   * still lands the user where the button promised.
   */
  defaultMode?: ComposerMode
  /**
   * Whether starting a task should navigate to its page. The home screen keeps
   * this on; the create dialog turns it off and offers a toast link instead so
   * the user can stay put and start another task.
   */
  navigateToTask?: boolean
}

/**
 * The single "start a task or session" composer, shared by the home screen and
 * the global create dialog so both surfaces cannot drift apart in either design
 * or behaviour.
 */
export function PromptComposer({
  onCreated,
  focusHotkey = false,
  defaultMode = 'implementation',
  navigateToTask = true,
}: PromptComposerProps) {
  const [workspace] = useCurrentWorkspace()
  const zero = useZero()
  const navigate = useNavigate()

  const editorRef = useRef<PromptEditorRef>(null)
  const [prompt, setPrompt] = useState('')
  const [selectedRepoOverride, setSelectedRepoOverride] = useState<
    string[] | null
  >(null)
  const [branchChoice, setBranchChoice] = useState<BranchChoice | null>(null)
  const [modelOverride, setModelOverride] = useState<string[] | null>(null)
  const [blueprintOverride, setBlueprintOverride] = useState<string | null>(
    null,
  )
  const [autoAcceptPlan, setAutoAcceptPlan] = useState(true)
  const [mode, setMode] = useState<ComposerMode>(defaultMode)
  const [startingSession, setStartingSession] = useState(false)
  const [startingImplementation, setStartingImplementation] = useState(false)

  const { data: executionConfig } =
    api.implementations.executionConfig.useQuery()
  const defaultModelId = executionConfig?.defaultModel ?? ''

  const [blueprints] = useQuery(
    queries.blueprints.list({ workspaceId: workspace.id }),
  )

  const { repos, error: reposError } = useGitHubRepositories({
    workspaceId: workspace.id,
  })
  const [lastRepoId, setLastRepoId] = useLastSelectedRepository(workspace.id)
  const [, setLastModelId] = useLastSelectedModel()
  const [user] = useCurrentUser()

  // Default repo selection derived from the stored "last selected" repo, until
  // the user explicitly changes it.
  const defaultSelectedRepo = useMemo(
    () => resolveDefaultRepoSelection(lastRepoId, repos),
    [lastRepoId, repos],
  )
  const selectedRepo = selectedRepoOverride ?? defaultSelectedRepo
  // A picked branch only survives while its repo stays selected — the repo above
  // can change underneath us when another composer updates the shared
  // "last selected repository" setting.
  const selectedBranch = resolveSelectedBranch(branchChoice, selectedRepo)

  // The **Default model** selector initially shows what a task without an
  // explicit model would actually run with: the user's configured default,
  // else the platform default. An explicit in-session pick keeps winning even
  // when the execution config resolves later and changes what the default
  // would be — and only that explicit pick is submitted as the task's model.
  const userDefaultModel = user.profile?.defaultModel
  const defaultSelectedModel = useMemo(
    () =>
      resolveInheritedDefaultModelSelection(
        userDefaultModel,
        defaultModelId,
        executionConfig?.models ?? [],
      ),
    [userDefaultModel, defaultModelId, executionConfig?.models],
  )
  const model = modelOverride ?? defaultSelectedModel
  const composerBlueprints = useMemo(
    () => uniqueBlueprintsByName(blueprints),
    [blueprints],
  )
  const blueprintId = resolveBlueprintId(
    blueprintOverride ?? undefined,
    composerBlueprints,
  )
  const selectedBlueprint = composerBlueprints.find(
    (bp) => bp.id === blueprintId,
  )
  const isAutoBlueprint = blueprintId === AUTO_BLUEPRINT_ID
  const SelectedBlueprintIcon = selectedBlueprint
    ? (blueprintTypeIcons[selectedBlueprint.type] ?? CodeCircleIcon)
    : null
  const showAutoAcceptPlan = shouldShowAutoAcceptPlan(mode, selectedBlueprint)

  const selectedRepoEntry = selectedRepo[0]
    ? repos.find((r) => r.id.toString() === selectedRepo[0])
    : undefined
  const [owner, repoName] = selectedRepoEntry?.full_name.split('/') ?? []

  const { data: branchesData, isLoading: branchesLoading } =
    api.github.branches.useQuery(
      {
        installationId: selectedRepoEntry?.installationId ?? 0,
        owner: owner ?? '',
        repo: repoName ?? '',
      },
      { enabled: !!selectedRepoEntry && !!owner && !!repoName },
    )
  const branches = branchesData?.branches ?? []

  const blueprintCollection = useMemo(
    () =>
      createListCollection({
        // The static Auto entry leads the catalog so `ValueText` can stringify
        // the `'auto'` sentinel and the option is part of keyboard navigation.
        items: [autoBlueprintItem, ...composerBlueprints],
        itemToString: (item) => item.name,
        itemToValue: (item) => item.id,
      }),
    [composerBlueprints],
  )

  // Selects restore focus to their trigger on close; defer so the editor
  // wins the focus race.
  const refocusEditor = () => {
    setTimeout(() => editorRef.current?.focus(), 50)
  }

  useHotkeysShortcut(
    'general.focusPrompt',
    () => {
      if (!focusHotkey) return
      editorRef.current?.focus()
    },
    [focusHotkey],
  )

  const handleRepoChange = (value: string[]) => {
    setSelectedRepoOverride(value)
    const repoId = value[0]
    const branch = defaultBranchSelection(repoId, repos)[0]
    setBranchChoice(repoId && branch ? { repoId, branch } : null)
    if (repoId) {
      setLastRepoId(repoId)
    }
  }

  const buildRepos = () =>
    buildRepoSelection(
      selectedRepo,
      repos,
      resolveBaseBranchById(selectedRepo, selectedBranch),
    )

  const handleStartSession = async () => {
    if (startingSession) return
    const modelId = model[0]
    // `mod-enter` submits straight past the button's `disabled` state, so the
    // requirements are re-checked here. Keep in step with `isStartDisabled`.
    if (!selectedRepo[0] || !modelId) return

    const reposPayload = buildRepos()
    if (reposPayload.length === 0) return

    const text = editorRef.current?.getText() ?? prompt

    setStartingSession(true)
    try {
      const sessionId = createId()
      await awaitServerMutation(
        zero.mutate(
          mutators.sessions.create({
            id: sessionId,
            workspaceId: workspace.id,
            repos: reposPayload,
            model: modelId,
          }),
        ),
      )

      // The session exists now, so this model was genuinely used to *start*
      // something — which is the event the preference records. Deliberately not
      // on dropdown change: browsing the picker and walking away should leave a
      // working preference alone. Deliberately inside the `try` and after the
      // await, so a start that never happened is never remembered. And
      // deliberately before the optional first message below, since a failed
      // `postMessage` does not un-start the session.
      setLastModelId(modelId)

      if (text.trim()) {
        await awaitServerMutation(
          zero.mutate(
            mutators.sessions.postMessage({
              id: createId(),
              workspaceId: workspace.id,
              runId: sessionId,
              text: text.trim(),
              mentions: [{ type: 'agent' }],
            }),
          ),
        )
      }

      onCreated?.()

      navigate({
        to: '/$workspace/sessions/$id',
        params: { workspace: workspace.slug, id: sessionId },
      })
    } catch (error: unknown) {
      toast.error({
        title: 'Failed to start session',
        description: error instanceof Error ? error.message : undefined,
      })
    } finally {
      setStartingSession(false)
    }
  }

  const handleStartImplementation = async () => {
    if (startingImplementation) return
    const modelId = model[0]
    const text = editorRef.current?.getText() ?? prompt
    // See `handleStartSession` — `mod-enter` bypasses the disabled button.
    if (!text.trim() || !selectedRepo[0] || !modelId || !blueprintId) return

    const reposPayload = buildRepos()
    if (reposPayload.length === 0) return

    setStartingImplementation(true)
    try {
      const implementationId = createId()
      await awaitServerMutation(
        zero.mutate(
          mutators.implementations.startAdHoc({
            id: implementationId,
            prompt: text.trim(),
            blueprintId,
            repos: reposPayload,
            workspaceId: workspace.id,
            model: resolveSubmittedTaskModel({
              explicitlySelected: modelOverride !== null,
              selectedModelId: modelId,
            }),
            stepConfigOverrides: resolveStepConfigOverrides(
              mode,
              selectedBlueprint,
              autoAcceptPlan,
            ),
          }),
        ),
      )

      // Same rule as `handleStartSession`: record only once the start actually
      // succeeded. Both tabs drive the one `ModelSelect`, so both must write the
      // preference or the single control would remember asymmetrically.
      setLastModelId(modelId)

      const taskPath = {
        to: '/$workspace/tasks/$id' as const,
        params: { workspace: workspace.slug, id: implementationId },
      }

      toast.success({
        title: 'Task started',
        ...(!navigateToTask && {
          action: {
            label: 'View',
            onClick: () => navigate(taskPath),
          },
        }),
      })

      onCreated?.()

      if (navigateToTask) {
        navigate(taskPath)
      }
    } catch (error: unknown) {
      toast.error({
        title: 'Failed to start task',
        description: error instanceof Error ? error.message : undefined,
      })
    } finally {
      setStartingImplementation(false)
    }
  }

  const handleStart = () => {
    if (mode === 'session') {
      return handleStartSession()
    }
    return handleStartImplementation()
  }

  const starting = startingSession || startingImplementation
  const startDisabled = isStartDisabled({
    mode,
    prompt,
    repoId: selectedRepo[0],
    modelId: model[0],
    blueprintId,
  })

  if (reposError) {
    return (
      <Card.Root
        size="lg"
        borderRadius="panel.lg"
        borderColor="border.emphasized"
        boxShadow="lg"
        p="3"
      >
        <Stack gap="4" align="center" py="8">
          <Text color="fg.muted" textAlign="center" maxW="md">
            Connect your GitHub account to start a task or session.
          </Text>
          <Button
            colorPalette="gray"
            variant="outline"
            onClick={() => {
              window.open(
                `/api/integrations/github/install?workspaceId=${workspace.id}`,
                'github-install',
                'width=600,height=700',
              )
            }}
          >
            Connect GitHub
          </Button>
        </Stack>
      </Card.Root>
    )
  }

  return (
    <Box
      position="relative"
      zIndex="1"
      borderWidth="1px"
      borderColor="border.emphasized"
      borderRadius="panel.lg"
      boxShadow="sm"
      bg="bg.panel"
      _focusWithin={{
        borderColor: 'gray.focusRing/40',
      }}
    >
      <Box borderBottomWidth="1px" py="1" px="2">
        <Tabs.Root
          value={mode}
          onValueChange={(e) => {
            if (e.value === 'implementation' || e.value === 'session') {
              setMode(e.value)
            }
          }}
          size="xs"
          variant="ghost"
        >
          <Tabs.List>
            <Tabs.Trigger value="implementation">Task</Tabs.Trigger>
            <Tabs.Trigger value="session">Session</Tabs.Trigger>
          </Tabs.List>
        </Tabs.Root>
      </Box>
      <Box px="3" py="2">
        <PromptEditor
          ref={editorRef}
          placeholder="What would you like to build?"
          onChange={setPrompt}
          onSubmit={handleStart}
          submitKey="mod-enter"
          autoFocus
          workspaceId={workspace.id}
        />
      </Box>
      <HStack
        justify="space-between"
        align="center"
        gap="2"
        px="2"
        py="1"
        pb="2"
      >
        <HStack gap="2" minW="0" hidden={mode !== 'implementation'}>
          <Field.Root width="auto">
            <Field.Label srOnly>Blueprint</Field.Label>
            <Select.Root
              collection={blueprintCollection}
              value={blueprintId ? [blueprintId] : []}
              onValueChange={(e) => {
                setBlueprintOverride(e.value[0] ?? null)
                refocusEditor()
              }}
              size="sm"
              variant="subtle"
              positioning={{ sameWidth: false }}
              width="auto"
            >
              <Select.HiddenSelect />
              <Select.Control>
                <Select.Trigger
                  justifyContent="flex-start"
                  gap="2"
                  borderRadius="full"
                  bg="transparent"
                  _hover={{ bg: 'bg.subtle', borderColor: 'transparent' }}
                >
                  {isAutoBlueprint ? (
                    <Infinity2Icon width="16" height="16" />
                  ) : (
                    SelectedBlueprintIcon && (
                      <SelectedBlueprintIcon width="16" height="16" />
                    )
                  )}
                  <Select.ValueText placeholder="Blueprint" />
                </Select.Trigger>
              </Select.Control>
              <Select.Positioner>
                <Select.Content maxW="300px">
                  <Select.ItemGroup>
                    <Select.ItemGroupLabel>Blueprint</Select.ItemGroupLabel>
                    <Select.Item
                      item={autoBlueprintItem}
                      gap="2"
                      justifyContent="flex-start"
                    >
                      <Infinity2Icon width="16" height="16" />
                      {autoBlueprintItem.name}
                    </Select.Item>
                    {composerBlueprints.map((bp) => {
                      const Icon = blueprintTypeIcons[bp.type] ?? CodeCircleIcon
                      return (
                        <Select.Item
                          key={bp.id}
                          item={bp}
                          gap="2"
                          justifyContent="flex-start"
                        >
                          <Icon width="16" height="16" />
                          {bp.name}
                        </Select.Item>
                      )
                    })}
                  </Select.ItemGroup>
                </Select.Content>
              </Select.Positioner>
            </Select.Root>
          </Field.Root>

          {showAutoAcceptPlan && (
            <Switch
              size="xs"
              checked={autoAcceptPlan}
              onCheckedChange={({ checked }) => setAutoAcceptPlan(!!checked)}
              whiteSpace="nowrap"
              color="fg.muted"
            >
              Auto accept
            </Switch>
          )}
        </HStack>
        <Spacer />
        {/* Only the selects wrap; the submit button stays a sibling so it can
            never be pushed onto a second row. */}
        <HStack justify="flex-start" flexWrap="wrap" gap="2">
          <Group>
            <Field.Root>
              <Field.Label srOnly>Repository</Field.Label>
              <RepositorySelect
                repos={repos}
                value={selectedRepo}
                onValueChange={(e) => handleRepoChange(e.value)}
              />
            </Field.Root>

            <Field.Root hidden={selectedRepo.length > 1}>
              <Field.Label srOnly>Base branch</Field.Label>
              <BranchSelect
                branches={branches}
                value={selectedBranch}
                onValueChange={(e) => {
                  const repoId = selectedRepo[0]
                  const branch = e.value[0]
                  setBranchChoice(repoId && branch ? { repoId, branch } : null)
                  refocusEditor()
                }}
                // `||`, not `??`: repos with no recorded default branch map to
                // an empty string, which would render a blank placeholder.
                placeholder={selectedRepoEntry?.default_branch || 'main'}
                disabled={!selectedRepoEntry || branchesLoading}
              />
            </Field.Root>
          </Group>

          <Field.Root width="auto">
            {/* For tasks this is the *default* model: it fills blueprint steps
                without their own model and never overrides an explicit
                blueprint step model. The displayed inherited value is only
                persisted when the user explicitly selects it. */}
            <Field.Label srOnly>
              {mode === 'implementation' ? 'Default model' : 'Model'}
            </Field.Label>
            <ModelSelect
              value={model}
              defaultModelId={
                mode === 'implementation' ? defaultSelectedModel[0] : undefined
              }
              isUsingDefault={
                mode === 'implementation' && modelOverride === null
              }
              onValueChange={(e) => {
                setModelOverride(e.value)
                refocusEditor()
              }}
              onUseDefault={
                mode === 'implementation'
                  ? () => {
                      setModelOverride(null)
                      refocusEditor()
                    }
                  : undefined
              }
            />
          </Field.Root>
        </HStack>
        <IconButton
          size="sm"
          variant="solid"
          colorPalette="emerald"
          borderRadius="full"
          aria-label={mode === 'session' ? 'Start session' : 'Start task'}
          onClick={handleStart}
          loading={starting}
          disabled={startDisabled}
        >
          <ArrowUpIcon />
        </IconButton>
      </HStack>
    </Box>
  )
}
